/**
 * Encapsulates the happy flow modal save logic including API calls,
 * analytics tracking, navigation, and error handling.
 *
 * @module coverageInfo/components/coverage/hooks/useHappyFlowSave
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { markOrderAsCompleted } from "../../../../../api/postCall/commentRpaModalSaveForOrderIds";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import { DEFAULT_INSURANCE_ISSUE_TEXT } from "../../../../../constants/stringLiterals";
import { useBusniessOfficeNotesContext } from "../../../../../context/busniessOfficeNotesContext";
import { useDataAccumulatorForSingleOrder } from "../../../../../context/dataAccumulatorForSingleOrder";
import { Status } from "../../../../../data-model/medicalPaOrdersModel";
import { SingleOrderDetailsTableName } from "../../../../../enums/singleOrderDetailsTableName";
import { UserGeneralInfoContextKeys } from "../../../../../enums/userGeneralInfoContextKeys";
import { useUpdateOrderDetails } from "../../../../../hooks/singleMedicalPaOrderDetails/useUpdateOrderDetails";
import { closeModal } from "../../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import { UserStore } from "../../../../../store/UserGeneralInfoStore";
import {
  getAssigneeDisplayName,
  insertDrugStatusFlippedTrailCommentIfChanged,
  insertOrderAssignedReassignedTrailComment,
  resolvePersistedAuthStatusId,
} from "../../../../../utils/orderTrailComment";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import { showApiError } from "../../../../../utils/showApiError";
import { CoverageAnalytics } from "./useCoverageAnalytics";

/**
 * Inputs for {@link useHappyFlowSave}: order context and analytics helper.
 *
 * @property analytics - Coverage analytics facade for BigQuery-style events
 * @property orderId - Medical PA order id for API payloads
 * @property status - Order `status` slice used for BO status comparison
 */
interface HappyFlowSaveProps {
  analytics: CoverageAnalytics;
  orderId: string;
  status: Status;
}
/**
 * Manages the save handler for the HappyFlow modal, including loading state,
 * provider selection, BO status computation, analytics tracking, and the
 * API call sequence (commentRpaModalSaveForOrderIds → provider reassignment).
 *
 * @param analytics - The coverage analytics hook instance for event tracking
 * @returns Save handler, loading state, BO status, and provider selection ref
 */
export const useHappyFlowSave = ({
  analytics,
  orderId,
  status,
}: HappyFlowSaveProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const selectedProvider = useRef<{ id: string; name: string }>({
    id: "",
    name: "",
  });

  const { runUpdate: runUpdateGeneralOrderDetails } = useUpdateOrderDetails({
    orderId: orderId,
    orgId: getOrgIdForFetchExternalWorklist(),
    tableName: SingleOrderDetailsTableName.MEDICAL_PA_ORDER_GENERAL_INFO,
  });
  const { data: singleHealthFacilityDetails } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { generalOrderDetails, authOnFileDetails } =
    useDataAccumulatorForSingleOrder();
  const { manualAuthStatus } = useBusniessOfficeNotesContext();
  const org_id = getOrgIdForFetchExternalWorklist();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const boStatus = useMemo(
    () =>
      singleHealthFacilityDetails?.inactive_insurance_bo_status ??
      DEFAULT_INSURANCE_ISSUE_TEXT,
    [singleHealthFacilityDetails?.inactive_insurance_bo_status],
  );

  /** @returns `true` when a non-empty provider has been selected in the ref. */
  const hasProviderSelection = () => {
    const { id, name } = selectedProvider.current;
    return id.trim() !== "" || name.trim() !== "";
  };

  /** Fires the mark-as-completed BigQuery event and delayed J-code status log. */
  const fireAnalytics = () => {
    analytics.fireMarkAsCompletedEvent(
      analytics.ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
      analytics.ModalNamesForBigQuery.HAPPY_FLOW_MODAL,
      analytics.masterAuthStatus,
    );
    analytics.logJCodesAfterSave(1000);
  };

  /**
   * Handles the HappyFlow modal save: fires analytics, persists BO data,
   * optionally assigns a provider after the save succeeds, then closes the
   * modal and navigates to worklists.
   *
   * @param selectedOrderIds - Optional linked sibling order ids from the modal
   */
  const handleSave = async (selectedOrderIds?: string[]) => {
    fireAnalytics();
    setIsLoading(true);

    try {
      await markOrderAsCompleted(
        {
          org_id: org_id ?? "",
          bo_status: boStatus,
          overwrite_rpa_errors: false,
          write_back_reports: null,
          auth_status: manualAuthStatus ?? undefined,
        },
        orderId ?? "",
        selectedOrderIds,
      );

      if (hasProviderSelection()) {
        const assignmentResult = await runUpdateGeneralOrderDetails({
          assigned_to_id: selectedProvider.current.id,
          assigned_to_name: selectedProvider.current.name,
        });

        if (assignmentResult.success) {
          const actingProviderName =
            `${providerDetails?.FirstName || ""} ${providerDetails?.LastName || ""}`.trim() ||
            "Unknown Provider";

          insertOrderAssignedReassignedTrailComment({
            orderId,
            orgId: org_id ?? "",
            patientId:
              generalOrderDetails?.order?.demographics?.patient_id ??
              generalOrderDetails?.order?.demographics?.patient_mrn,
            providerEmail: user?.email ?? "",
            providerName: actingProviderName,
            fromAssignee: getAssigneeDisplayName(
              generalOrderDetails?.order?.assigned_to?.assigned_to_id,
              generalOrderDetails?.order?.assigned_to?.assigned_to_name,
            ),
            toAssignee: selectedProvider.current.name,
          });
        }
      }

      const actingProviderName =
        `${providerDetails?.FirstName || ""} ${providerDetails?.LastName || ""}`.trim() ||
        "Unknown Provider";

      insertDrugStatusFlippedTrailCommentIfChanged({
        orderId,
        orgId: org_id ?? "",
        patientId: generalOrderDetails?.order?.demographics?.patient_id ?? "",
        providerEmail: user?.email ?? "",
        providerName: actingProviderName,
        fromStatusId: resolvePersistedAuthStatusId(authOnFileDetails),
        toStatusId: manualAuthStatus ?? undefined,
        authStatusOptions: authStatusOptions ?? [],
      });

      dispatch(closeModal());
      toast.success(ToastMessages.MARK_AS_COMPLETED_SUCCESS);
      navigate(
        `/worklists/${UserStore.getItem(UserGeneralInfoContextKeys.PARENT_TABLE) ?? ""}`,
      );
    } catch (error) {
      console.error("error", error);
      showApiError(error, ToastMessages.MARK_AS_COMPLETED_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  return { handleSave, isLoading, selectedProvider, boStatus };
};

/** Return type of the useHappyFlowSave hook. */
export type HappyFlowSave = ReturnType<typeof useHappyFlowSave>;
